import type { Renderer } from '../base/Renderer';
import type { SimulationState, Coordinate } from '../../core/types/SimulationTypes';

export class VoxelWebGLRenderer implements Renderer {
  private canvas: HTMLCanvasElement | null = null;
  private gl: WebGLRenderingContext | null = null;
  private container: HTMLElement | null = null;

  private program: WebGLProgram | null = null;
  private voxelBuffer: WebGLBuffer | null = null;
  private normalBuffer: WebGLBuffer | null = null;
  private instanceOffsetBuffer: WebGLBuffer | null = null;
  private instanceColorBuffer: WebGLBuffer | null = null;
  private instanceAOBuffer: WebGLBuffer | null = null;
  private instancingExt: ANGLE_instanced_arrays | null = null;

  // Particle rendering
  private particleProgram: WebGLProgram | null = null;
  private particleBuffer: WebGLBuffer | null = null;
  private particleInstanceBuffer: WebGLBuffer | null = null;
  private particleColorBuffer: WebGLBuffer | null = null;

  private gridWidth: number = 0;
  private gridHeight: number = 0;

  private cameraAngle: number = Math.PI / 4;
  private cameraDistance: number = 150;
  private cameraHeight: number = 100;

  private isDragging: boolean = false;
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;

  private panX: number = 0;
  private panY: number = 0;
  private zoomLevel: number = 1.0;

  public initialize(container: HTMLElement): void {
    this.container = container;

    this.canvas = document.createElement('canvas');
    this.gl = this.canvas.getContext('webgl');

    if (!this.gl) {
      throw new Error('WebGL not supported');
    }

    container.appendChild(this.canvas);
    this.resize(container.clientWidth, container.clientHeight);

    this.setupWebGL();
    this.setupEventListeners();

    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => {
    if (this.container) {
      this.resize(this.container.clientWidth, this.container.clientHeight);
    }
  };

  public resize(width: number, height: number): void {
    if (!this.canvas || !this.gl) return;

    this.canvas.width = width;
    this.canvas.height = height;
    this.gl.viewport(0, 0, width, height);
  }

  private setupWebGL(): void {
    const gl = this.gl;
    if (!gl) return;

    // Get instancing extension
    this.instancingExt = gl.getExtension('ANGLE_instanced_arrays');
    if (!this.instancingExt) {
      console.warn('ANGLE_instanced_arrays not supported, falling back to slower rendering');
    }

    // Vertex shader with instancing and all effects
    const vertexShaderSource = `
      attribute vec3 aPosition;
      attribute vec3 aNormal;
      attribute vec3 aInstanceOffset;
      attribute vec3 aInstanceColor;
      attribute float aInstanceAO;

      uniform mat4 uProjection;
      uniform mat4 uView;
      uniform float uVoxelSize;
      uniform vec3 uLightDir;
      uniform vec3 uCameraPos;

      varying vec3 vColor;
      varying vec3 vNormal;
      varying vec3 vLightDir;
      varying vec3 vViewDir;
      varying float vAO;
      varying float vDepth;
      varying vec3 vWorldPos;

      void main() {
        vec3 worldPos = aPosition * uVoxelSize + aInstanceOffset;
        vec4 viewPos = uView * vec4(worldPos, 1.0);
        gl_Position = uProjection * viewPos;

        vColor = aInstanceColor;
        vNormal = aNormal;
        vLightDir = uLightDir;
        vViewDir = normalize(uCameraPos - worldPos);
        vAO = aInstanceAO;
        vDepth = -viewPos.z;
        vWorldPos = worldPos;
      }
    `;

    // Fragment shader with all visual enhancements
    const fragmentShaderSource = `
      precision mediump float;

      varying vec3 vColor;
      varying vec3 vNormal;
      varying vec3 vLightDir;
      varying vec3 vViewDir;
      varying float vAO;
      varying float vDepth;
      varying vec3 vWorldPos;

      uniform vec3 uFogColor;
      uniform float uFogNear;
      uniform float uFogFar;

      // Simple hash function for noise
      float hash(vec3 p) {
        p = fract(p * 0.3183099 + 0.1);
        p *= 17.0;
        return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
      }

      void main() {
        vec3 normal = normalize(vNormal);
        vec3 lightDir = normalize(vLightDir);
        vec3 viewDir = normalize(vViewDir);

        // Diffuse lighting
        float diffuse = max(dot(normal, lightDir), 0.0);

        // Rim lighting (edge highlighting)
        float rim = 1.0 - max(dot(viewDir, normal), 0.0);
        rim = pow(rim, 3.0) * 0.3;

        // Check if this is water
        float isWater = step(0.5, vColor.b) * step(vColor.b, vColor.g);

        // Specular highlights (only on water)
        vec3 halfDir = normalize(lightDir + viewDir);
        float specular = pow(max(dot(normal, halfDir), 0.0), 32.0) * isWater * 0.5;

        // Color variation noise (subtle, not on water)
        float noise = hash(vWorldPos * 0.5) * 0.05 * (1.0 - isWater);
        vec3 colorWithNoise = vColor + noise;

        // Ambient + diffuse + rim + specular
        float ambient = 0.6;
        float lighting = ambient + diffuse * 0.5 + rim;

        // Apply ambient occlusion
        lighting *= vAO;

        vec3 finalColor = colorWithNoise * lighting + vec3(specular);

        // Atmospheric fog
        float fogFactor = smoothstep(uFogNear, uFogFar, vDepth);
        finalColor = mix(finalColor, uFogColor, fogFactor);

        float alpha = mix(1.0, 0.7, isWater);

        gl_FragColor = vec4(finalColor, alpha);
      }
    `;

    const vertexShader = this.createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = this.createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);

    if (!vertexShader || !fragmentShader) {
      throw new Error('Failed to create shaders');
    }

    this.program = gl.createProgram();
    if (!this.program) {
      throw new Error('Failed to create program');
    }

    gl.attachShader(this.program, vertexShader);
    gl.attachShader(this.program, fragmentShader);
    gl.linkProgram(this.program);

    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
      throw new Error('Program link failed: ' + gl.getProgramInfoLog(this.program));
    }

    // Create voxel cube geometry
    const geometry = this.createVoxelGeometry();

    this.voxelBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.voxelBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(geometry.positions), gl.STATIC_DRAW);

    this.normalBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.normalBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(geometry.normals), gl.STATIC_DRAW);

    // Create instance buffers (will be updated each frame)
    this.instanceOffsetBuffer = gl.createBuffer();
    this.instanceColorBuffer = gl.createBuffer();
    this.instanceAOBuffer = gl.createBuffer();

    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.CULL_FACE);

    // Enable blending for water transparency
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    // Setup particle rendering
    this.setupParticleShader();
  }

  // Helper method for buffer operations
  private updateBuffer(buffer: WebGLBuffer | null, data: Float32Array): void {
    const gl = this.gl;
    if (!gl || !buffer) return;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
  }

  // Helper method for setting up vertex attributes
  private setupAttribute(
    program: WebGLProgram,
    name: string,
    buffer: WebGLBuffer,
    size: number,
    divisor: number = 0
  ): number {
    const gl = this.gl;
    if (!gl) return -1;

    const loc = gl.getAttribLocation(program, name);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);

    if (divisor > 0 && this.instancingExt) {
      this.instancingExt.vertexAttribDivisorANGLE(loc, divisor);
    }

    return loc;
  }

  // Helper method for calculating sand color
  private calculateSandColor(
    z: number,
    wetnessFactor: number,
    isUnderwater: boolean
  ): [number, number, number] {
    const brightness = isUnderwater ? 0.7 + (z * 0.01) : 0.85 + (z * 0.02);
    const baseR = isUnderwater ? 210 : 245;
    const baseG = isUnderwater ? 190 : 215;
    const baseB = isUnderwater ? 130 : 140;

    return [
      Math.min(1.0, (baseR * brightness * wetnessFactor) / 255),
      Math.min(1.0, (baseG * brightness * wetnessFactor) / 255),
      Math.min(1.0, (baseB * brightness * wetnessFactor) / 255)
    ];
  }

  private setupParticleShader(): void {
    const gl = this.gl;
    if (!gl) return;

    // Simple particle shader - billboard quads
    const particleVertexSource = `
      attribute vec2 aPosition;
      attribute vec3 aParticlePos;
      attribute vec4 aParticleColor;
      attribute float aParticleSize;

      uniform mat4 uProjection;
      uniform mat4 uView;
      uniform vec3 uCameraRight;
      uniform vec3 uCameraUp;

      varying vec4 vColor;
      varying vec2 vUV;

      void main() {
        // Billboard - always face camera
        vec3 vertexPos = aParticlePos
          + uCameraRight * aPosition.x * aParticleSize
          + uCameraUp * aPosition.y * aParticleSize;

        gl_Position = uProjection * uView * vec4(vertexPos, 1.0);
        vColor = aParticleColor;
        vUV = aPosition * 0.5 + 0.5;
      }
    `;

    const particleFragmentSource = `
      precision mediump float;

      varying vec4 vColor;
      varying vec2 vUV;

      void main() {
        // Circular particle
        float dist = distance(vUV, vec2(0.5, 0.5));
        if (dist > 0.5) discard;

        float alpha = (1.0 - dist * 2.0) * vColor.a;
        gl_FragColor = vec4(vColor.rgb, alpha);
      }
    `;

    const particleVertShader = this.createShader(gl, gl.VERTEX_SHADER, particleVertexSource);
    const particleFragShader = this.createShader(gl, gl.FRAGMENT_SHADER, particleFragmentSource);

    if (!particleVertShader || !particleFragShader) return;

    this.particleProgram = gl.createProgram();
    if (!this.particleProgram) return;

    gl.attachShader(this.particleProgram, particleVertShader);
    gl.attachShader(this.particleProgram, particleFragShader);
    gl.linkProgram(this.particleProgram);

    if (!gl.getProgramParameter(this.particleProgram, gl.LINK_STATUS)) {
      console.error('Particle program link failed:', gl.getProgramInfoLog(this.particleProgram));
      return;
    }

    // Create particle quad
    const particleQuad = new Float32Array([
      -1, -1,
       1, -1,
       1,  1,
      -1, -1,
       1,  1,
      -1,  1
    ]);

    this.particleBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this.particleBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, particleQuad, gl.STATIC_DRAW);

    // Instance buffers for particles
    this.particleInstanceBuffer = gl.createBuffer();
    this.particleColorBuffer = gl.createBuffer();
  }

  private createShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
    const shader = gl.createShader(type);
    if (!shader) return null;

    gl.shaderSource(shader, source);
    gl.compileShader(shader);

    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('Shader compile failed:', gl.getShaderInfoLog(shader));
      gl.deleteShader(shader);
      return null;
    }

    return shader;
  }

  private createVoxelGeometry(): { positions: number[], normals: number[] } {
    // Cube vertices with normals (36 vertices for 6 faces, 2 triangles each)
    const positions = [
      // Front face (z = 0.5)
      -0.5, -0.5,  0.5,  0.5, -0.5,  0.5,  0.5,  0.5,  0.5,
      -0.5, -0.5,  0.5,  0.5,  0.5,  0.5, -0.5,  0.5,  0.5,

      // Back face (z = -0.5)
      -0.5, -0.5, -0.5, -0.5,  0.5, -0.5,  0.5,  0.5, -0.5,
      -0.5, -0.5, -0.5,  0.5,  0.5, -0.5,  0.5, -0.5, -0.5,

      // Top face (y = 0.5)
      -0.5,  0.5, -0.5, -0.5,  0.5,  0.5,  0.5,  0.5,  0.5,
      -0.5,  0.5, -0.5,  0.5,  0.5,  0.5,  0.5,  0.5, -0.5,

      // Bottom face (y = -0.5)
      -0.5, -0.5, -0.5,  0.5, -0.5, -0.5,  0.5, -0.5,  0.5,
      -0.5, -0.5, -0.5,  0.5, -0.5,  0.5, -0.5, -0.5,  0.5,

      // Right face (x = 0.5)
       0.5, -0.5, -0.5,  0.5,  0.5, -0.5,  0.5,  0.5,  0.5,
       0.5, -0.5, -0.5,  0.5,  0.5,  0.5,  0.5, -0.5,  0.5,

      // Left face (x = -0.5)
      -0.5, -0.5, -0.5, -0.5, -0.5,  0.5, -0.5,  0.5,  0.5,
      -0.5, -0.5, -0.5, -0.5,  0.5,  0.5, -0.5,  0.5, -0.5,
    ];

    const normals = [
      // Front face normals
      0, 0, 1,  0, 0, 1,  0, 0, 1,
      0, 0, 1,  0, 0, 1,  0, 0, 1,

      // Back face normals
      0, 0, -1,  0, 0, -1,  0, 0, -1,
      0, 0, -1,  0, 0, -1,  0, 0, -1,

      // Top face normals
      0, 1, 0,  0, 1, 0,  0, 1, 0,
      0, 1, 0,  0, 1, 0,  0, 1, 0,

      // Bottom face normals
      0, -1, 0,  0, -1, 0,  0, -1, 0,
      0, -1, 0,  0, -1, 0,  0, -1, 0,

      // Right face normals
      1, 0, 0,  1, 0, 0,  1, 0, 0,
      1, 0, 0,  1, 0, 0,  1, 0, 0,

      // Left face normals
      -1, 0, 0,  -1, 0, 0,  -1, 0, 0,
      -1, 0, 0,  -1, 0, 0,  -1, 0, 0,
    ];

    return { positions, normals };
  }

  private setupEventListeners(): void {
    if (!this.canvas) return;

    this.canvas.addEventListener('mousedown', this.handleMouseDown);
    this.canvas.addEventListener('mousemove', this.handleMouseMove);
    this.canvas.addEventListener('mouseup', this.handleMouseUp);
    this.canvas.addEventListener('wheel', this.handleWheel);
  }

  private handleMouseDown = (event: MouseEvent): void => {
    if (event.button === 1) {
      this.isDragging = true;
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      event.preventDefault();
    }
  };

  private handleMouseMove = (event: MouseEvent): void => {
    if (this.isDragging) {
      const deltaX = event.clientX - this.lastMouseX;
      const deltaY = event.clientY - this.lastMouseY;

      this.cameraAngle -= deltaX * 0.01;
      this.cameraHeight = Math.max(20, Math.min(200, this.cameraHeight - deltaY * 0.5));

      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
    }
  };

  private handleMouseUp = (): void => {
    this.isDragging = false;
  };

  private handleWheel = (event: WheelEvent): void => {
    event.preventDefault();
    this.cameraDistance = Math.max(50, Math.min(300, this.cameraDistance + event.deltaY * 0.1));
  };

  private calculateAO(x: number, y: number, z: number, sandGrid: any, waterGrid: any): number {
    // Check 6 neighbors and count how many are solid (have sand or water)
    let occluded = 0;
    const checks = [
      [x + 1, y, z], [x - 1, y, z],
      [x, y + 1, z], [x, y - 1, z],
      [x, y, z + 1], [x, y, z - 1]
    ];

    for (const [nx, ny, nz] of checks) {
      if (nx < 0 || nx >= this.gridWidth || ny < 0 || ny >= this.gridHeight) continue;

      const neighborSandHeight = Math.floor(sandGrid.get(nx, ny));
      const neighborWaterHeight = waterGrid?.getHeight(nx, ny) || 0;
      const neighborTop = Math.max(neighborSandHeight, neighborWaterHeight);

      if (nz <= neighborTop) {
        occluded++;
      }
    }

    // Map 0-6 neighbors to 0.7-1.0 AO factor (less aggressive)
    return 0.7 + (6 - occluded) / 6 * 0.3;
  }

  public render(state: SimulationState): void {
    const gl = this.gl;
    if (!gl || !this.program || !this.canvas) return;

    const { sandGrid, waterGrid, wetSandTracker, metadata } = state;
    const { gridWidth, gridHeight } = metadata;

    this.gridWidth = gridWidth;
    this.gridHeight = gridHeight;
    this.currentSandGrid = sandGrid;

    // Bright summer sky
    gl.clearColor(0.53, 0.81, 0.98, 1.0);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    gl.useProgram(this.program);

    // Set up matrices
    const aspect = this.canvas.width / this.canvas.height;
    const projection = this.createPerspectiveMatrix(Math.PI / 4, aspect, 1, 1000);
    const view = this.createViewMatrix();

    const projLoc = gl.getUniformLocation(this.program, 'uProjection');
    const viewLoc = gl.getUniformLocation(this.program, 'uView');
    const voxelSizeLoc = gl.getUniformLocation(this.program, 'uVoxelSize');
    const lightDirLoc = gl.getUniformLocation(this.program, 'uLightDir');
    const cameraPosLoc = gl.getUniformLocation(this.program, 'uCameraPos');
    const fogColorLoc = gl.getUniformLocation(this.program, 'uFogColor');
    const fogNearLoc = gl.getUniformLocation(this.program, 'uFogNear');
    const fogFarLoc = gl.getUniformLocation(this.program, 'uFogFar');

    gl.uniformMatrix4fv(projLoc, false, projection);
    gl.uniformMatrix4fv(viewLoc, false, view);
    gl.uniform1f(voxelSizeLoc, 1.0 * this.zoomLevel);
    // Bright overhead summer sun
    gl.uniform3f(lightDirLoc, 0.3, 1.0, 0.2);

    // Camera position
    const centerX = 0;
    const centerZ = 0;
    const camX = centerX + Math.cos(this.cameraAngle) * this.cameraDistance + this.panX;
    const camY = this.cameraHeight + this.panY;
    const camZ = centerZ + Math.sin(this.cameraAngle) * this.cameraDistance;
    gl.uniform3f(cameraPosLoc, camX, camY, camZ);

    // Subtle summer haze fog
    gl.uniform3f(fogColorLoc, 0.53, 0.81, 0.98);
    gl.uniform1f(fogNearLoc, 200);
    gl.uniform1f(fogFarLoc, 400);

    // Collect instance data with AO
    const offsets: number[] = [];
    const colors: number[] = [];
    const aos: number[] = [];

    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        const height = Math.floor(sandGrid.get(x, y));
        const waterVolume = waterGrid?.getHeight(x, y) || 0;

        // Get wetness for this cell (darker = wetter)
        const wetness = wetSandTracker?.getWetness(x, y) || 0;
        const wetnessFactor = 1.0 - wetness * 0.4; // Up to 40% darker when fully wet

        // Add sand voxels (golden summer sand)
        if (height > 0) {
          for (let z = 0; z < height; z++) {
            const [r, g, b] = this.calculateSandColor(z, wetnessFactor, false);
            const ao = this.calculateAO(x, y, z, sandGrid, waterGrid);

            offsets.push(x - gridWidth / 2, z, y - gridHeight / 2);
            colors.push(r, g, b);
            aos.push(ao);
          }
        } 

        // Add water voxels (waterVolume is depth above sand)
        const waterBase = height; // Water starts at sand surface
        const waterTop = height + waterVolume; // Water extends depth above sand

        // Clamp water height to reasonable range to prevent array overflow
        const maxWaterVoxels = 50; // Maximum height of water column to render
        const waterHeight = Math.min(Math.floor(waterVolume), maxWaterVoxels);

        if (waterHeight > 0) {
          for (let z = 0; z < waterHeight; z++) {
            const actualZ = waterBase + z;
            const depthBelowSurface = waterTop - actualZ;
            const normalizedDepth = Math.min(depthBelowSurface / 10, 1);

            // Tropical turquoise water
            const shallowR = 0.4;
            const shallowG = 0.88;
            const shallowB = 0.95;
            const deepR = 0.0;
            const deepG = 0.5;
            const deepB = 0.8;

            const r = shallowR + (deepR - shallowR) * normalizedDepth;
            const g = shallowG + (deepG - shallowG) * normalizedDepth;
            const b = shallowB + (deepB - shallowB) * normalizedDepth;

            const ao = this.calculateAO(x, y, actualZ, sandGrid, waterGrid);

            offsets.push(x - gridWidth / 2, actualZ, y - gridHeight / 2);
            colors.push(r, g, b);
            aos.push(ao);
          }
        }
      }
    }

    const voxelCount = offsets.length / 3;

    if (voxelCount === 0) return;

    // Upload instance data
    this.updateBuffer(this.instanceOffsetBuffer, new Float32Array(offsets));
    this.updateBuffer(this.instanceColorBuffer, new Float32Array(colors));
    this.updateBuffer(this.instanceAOBuffer, new Float32Array(aos));

    // Set up vertex attributes (non-instanced)
    this.setupAttribute(this.program!, 'aPosition', this.voxelBuffer!, 3, 0);
    this.setupAttribute(this.program!, 'aNormal', this.normalBuffer!, 3, 0);

    // Set up instance attributes
    const offsetLoc = this.setupAttribute(this.program!, 'aInstanceOffset', this.instanceOffsetBuffer!, 3, 1);
    const colorLoc = this.setupAttribute(this.program!, 'aInstanceColor', this.instanceColorBuffer!, 3, 1);
    const aoLoc = this.setupAttribute(this.program!, 'aInstanceAO', this.instanceAOBuffer!, 1, 1);

    // Draw all instances in one call
    if (this.instancingExt) {
      this.instancingExt.drawArraysInstancedANGLE(gl.TRIANGLES, 0, 36, voxelCount);

      // Reset divisors
      this.instancingExt.vertexAttribDivisorANGLE(offsetLoc, 0);
      this.instancingExt.vertexAttribDivisorANGLE(colorLoc, 0);
      this.instancingExt.vertexAttribDivisorANGLE(aoLoc, 0);
    } else {
      // Fallback to non-instanced rendering
      for (let i = 0; i < voxelCount; i++) {
        gl.drawArrays(gl.TRIANGLES, 0, 36);
      }
    }

    // Render particles
    this.renderParticles(state, projection, view, camX, camY, camZ);
  }

  private renderParticles(
    state: SimulationState,
    projection: Float32Array,
    view: Float32Array,
    camX: number,
    camY: number,
    camZ: number
  ): void {
    const gl = this.gl;
    if (!gl || !this.particleProgram || !this.instancingExt) return;

    const { foamParticles, dustParticles } = state;

    // Collect all particles
    const particleData: number[] = []; // x, y, z, r, g, b, a, size

    // Add foam particles (white, floating)
    if (foamParticles) {
      const foam = foamParticles.getParticles();
      for (const p of foam) {
        particleData.push(
          p.x, p.z, p.y,  // position
          p.r, p.g, p.b, p.alpha,  // color
          p.size
        );
      }
    }

    // Add dust particles (sandy color, falling)
    if (dustParticles) {
      const dust = dustParticles.getParticles();
      for (const p of dust) {
        particleData.push(
          p.x, p.z, p.y,  // position
          p.r, p.g, p.b, p.alpha,  // color
          p.size
        );
      }
    }

    const particleCount = particleData.length / 8;
    if (particleCount === 0) return;

    // Use particle shader
    gl.useProgram(this.particleProgram);

    // Set uniforms
    const projLoc = gl.getUniformLocation(this.particleProgram, 'uProjection');
    const viewLoc = gl.getUniformLocation(this.particleProgram, 'uView');
    gl.uniformMatrix4fv(projLoc, false, projection);
    gl.uniformMatrix4fv(viewLoc, false, view);

    // Calculate camera right and up vectors from view matrix
    const viewInv = this.invertMatrix(view);
    const cameraRight = [viewInv[0], viewInv[1], viewInv[2]];
    const cameraUp = [viewInv[4], viewInv[5], viewInv[6]];

    const rightLoc = gl.getUniformLocation(this.particleProgram, 'uCameraRight');
    const upLoc = gl.getUniformLocation(this.particleProgram, 'uCameraUp');
    gl.uniform3f(rightLoc, cameraRight[0], cameraRight[1], cameraRight[2]);
    gl.uniform3f(upLoc, cameraUp[0], cameraUp[1], cameraUp[2]);

    // Prepare instance data
    const positions: number[] = [];
    const colors: number[] = [];
    const sizes: number[] = [];

    for (let i = 0; i < particleCount; i++) {
      const base = i * 8;
      positions.push(particleData[base], particleData[base + 1], particleData[base + 2]);
      colors.push(
        particleData[base + 3],
        particleData[base + 4],
        particleData[base + 5],
        particleData[base + 6]
      );
      sizes.push(particleData[base + 7]);
    }

    // Upload instance data
    this.updateBuffer(this.particleInstanceBuffer, new Float32Array(positions));
    this.updateBuffer(this.particleColorBuffer, new Float32Array(colors));

    // Set up vertex attributes
    this.setupAttribute(this.particleProgram!, 'aPosition', this.particleBuffer!, 2, 0);
    const particlePosLoc = this.setupAttribute(this.particleProgram!, 'aParticlePos', this.particleInstanceBuffer!, 3, 1);
    const particleColorLoc = this.setupAttribute(this.particleProgram!, 'aParticleColor', this.particleColorBuffer!, 4, 1);

    // Set particle size as constant
    const particleSizeLoc = gl.getAttribLocation(this.particleProgram, 'aParticleSize');
    gl.disableVertexAttribArray(particleSizeLoc);
    gl.vertexAttrib1f(particleSizeLoc, 0.5);

    // Disable depth writes for particles (but still test depth)
    gl.depthMask(false);

    // Draw particles
    this.instancingExt!.drawArraysInstancedANGLE(gl.TRIANGLES, 0, 6, particleCount);

    // Re-enable depth writes
    gl.depthMask(true);

    // Reset divisors
    this.instancingExt!.vertexAttribDivisorANGLE(particlePosLoc, 0);
    this.instancingExt!.vertexAttribDivisorANGLE(particleColorLoc, 0);
  }

  private createPerspectiveMatrix(fov: number, aspect: number, near: number, far: number): Float32Array {
    const f = 1.0 / Math.tan(fov / 2);
    const rangeInv = 1.0 / (near - far);

    return new Float32Array([
      f / aspect, 0, 0, 0,
      0, f, 0, 0,
      0, 0, (near + far) * rangeInv, -1,
      0, 0, near * far * rangeInv * 2, 0
    ]);
  }

  private createViewMatrix(): Float32Array {
    const centerX = 0;
    const centerY = 0;
    const centerZ = 0;

    const camX = centerX + Math.cos(this.cameraAngle) * this.cameraDistance + this.panX;
    const camY = this.cameraHeight + this.panY;
    const camZ = centerZ + Math.sin(this.cameraAngle) * this.cameraDistance;

    // Simple lookAt matrix
    const eye = [camX, camY, camZ];
    const center = [centerX, centerY, centerZ];
    const up = [0, 1, 0];

    const zAxis = this.normalize([eye[0] - center[0], eye[1] - center[1], eye[2] - center[2]]);
    const xAxis = this.normalize(this.cross(up, zAxis));
    const yAxis = this.cross(zAxis, xAxis);

    return new Float32Array([
      xAxis[0], yAxis[0], zAxis[0], 0,
      xAxis[1], yAxis[1], zAxis[1], 0,
      xAxis[2], yAxis[2], zAxis[2], 0,
      -this.dot(xAxis, eye), -this.dot(yAxis, eye), -this.dot(zAxis, eye), 1
    ]);
  }

  private normalize(v: number[]): number[] {
    const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
    return [v[0] / len, v[1] / len, v[2] / len];
  }

  private cross(a: number[], b: number[]): number[] {
    return [
      a[1] * b[2] - a[2] * b[1],
      a[2] * b[0] - a[0] * b[2],
      a[0] * b[1] - a[1] * b[0]
    ];
  }

  private dot(a: number[], b: number[]): number {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  }

  private invertMatrix(m: Float32Array): Float32Array {
    const inv = new Float32Array(16);

    inv[0] = m[5] * m[10] * m[15] - m[5] * m[11] * m[14] - m[9] * m[6] * m[15] +
             m[9] * m[7] * m[14] + m[13] * m[6] * m[11] - m[13] * m[7] * m[10];
    inv[4] = -m[4] * m[10] * m[15] + m[4] * m[11] * m[14] + m[8] * m[6] * m[15] -
             m[8] * m[7] * m[14] - m[12] * m[6] * m[11] + m[12] * m[7] * m[10];
    inv[8] = m[4] * m[9] * m[15] - m[4] * m[11] * m[13] - m[8] * m[5] * m[15] +
             m[8] * m[7] * m[13] + m[12] * m[5] * m[11] - m[12] * m[7] * m[9];
    inv[12] = -m[4] * m[9] * m[14] + m[4] * m[10] * m[13] + m[8] * m[5] * m[14] -
              m[8] * m[6] * m[13] - m[12] * m[5] * m[10] + m[12] * m[6] * m[9];
    inv[1] = -m[1] * m[10] * m[15] + m[1] * m[11] * m[14] + m[9] * m[2] * m[15] -
             m[9] * m[3] * m[14] - m[13] * m[2] * m[11] + m[13] * m[3] * m[10];
    inv[5] = m[0] * m[10] * m[15] - m[0] * m[11] * m[14] - m[8] * m[2] * m[15] +
             m[8] * m[3] * m[14] + m[12] * m[2] * m[11] - m[12] * m[3] * m[10];
    inv[9] = -m[0] * m[9] * m[15] + m[0] * m[11] * m[13] + m[8] * m[1] * m[15] -
             m[8] * m[3] * m[13] - m[12] * m[1] * m[11] + m[12] * m[3] * m[9];
    inv[13] = m[0] * m[9] * m[14] - m[0] * m[10] * m[13] - m[8] * m[1] * m[14] +
              m[8] * m[2] * m[13] + m[12] * m[1] * m[10] - m[12] * m[2] * m[9];
    inv[2] = m[1] * m[6] * m[15] - m[1] * m[7] * m[14] - m[5] * m[2] * m[15] +
             m[5] * m[3] * m[14] + m[13] * m[2] * m[7] - m[13] * m[3] * m[6];
    inv[6] = -m[0] * m[6] * m[15] + m[0] * m[7] * m[14] + m[4] * m[2] * m[15] -
             m[4] * m[3] * m[14] - m[12] * m[2] * m[7] + m[12] * m[3] * m[6];
    inv[10] = m[0] * m[5] * m[15] - m[0] * m[7] * m[13] - m[4] * m[1] * m[15] +
              m[4] * m[3] * m[13] + m[12] * m[1] * m[7] - m[12] * m[3] * m[5];
    inv[14] = -m[0] * m[5] * m[14] + m[0] * m[6] * m[13] + m[4] * m[1] * m[14] -
              m[4] * m[2] * m[13] - m[12] * m[1] * m[6] + m[12] * m[2] * m[5];
    inv[3] = -m[1] * m[6] * m[11] + m[1] * m[7] * m[10] + m[5] * m[2] * m[11] -
             m[5] * m[3] * m[10] - m[9] * m[2] * m[7] + m[9] * m[3] * m[6];
    inv[7] = m[0] * m[6] * m[11] - m[0] * m[7] * m[10] - m[4] * m[2] * m[11] +
             m[4] * m[3] * m[10] + m[8] * m[2] * m[7] - m[8] * m[3] * m[6];
    inv[11] = -m[0] * m[5] * m[11] + m[0] * m[7] * m[9] + m[4] * m[1] * m[11] -
              m[4] * m[3] * m[9] - m[8] * m[1] * m[7] + m[8] * m[3] * m[5];
    inv[15] = m[0] * m[5] * m[10] - m[0] * m[6] * m[9] - m[4] * m[1] * m[10] +
              m[4] * m[2] * m[9] + m[8] * m[1] * m[6] - m[8] * m[2] * m[5];

    const det = m[0] * inv[0] + m[1] * inv[4] + m[2] * inv[8] + m[3] * inv[12];

    if (det === 0) return m; // Return original if not invertible

    const invDet = 1.0 / det;
    for (let i = 0; i < 16; i++) {
      inv[i] = inv[i] * invDet;
    }

    return inv;
  }

  private multiplyMatrixVector(m: Float32Array, v: number[]): number[] {
    return [
      m[0] * v[0] + m[4] * v[1] + m[8] * v[2] + m[12] * v[3],
      m[1] * v[0] + m[5] * v[1] + m[9] * v[2] + m[13] * v[3],
      m[2] * v[0] + m[6] * v[1] + m[10] * v[2] + m[14] * v[3],
      m[3] * v[0] + m[7] * v[1] + m[11] * v[2] + m[15] * v[3]
    ];
  }

  private currentSandGrid: any = null;

  public screenToWorld(screenX: number, screenY: number): Coordinate | null {
    if (!this.canvas || !this.gl || !this.currentSandGrid) return null;

    // Convert screen coordinates to normalized device coordinates (-1 to 1)
    const rect = this.canvas.getBoundingClientRect();
    const x = ((screenX - rect.left) / rect.width) * 2 - 1;
    const y = -(((screenY - rect.top) / rect.height) * 2 - 1);

    // Create ray from camera through mouse position
    const aspect = this.canvas.width / this.canvas.height;
    const projection = this.createPerspectiveMatrix(Math.PI / 4, aspect, 1, 1000);
    const view = this.createViewMatrix();

    // Invert matrices to get from clip space to world space
    const invProjection = this.invertMatrix(projection);
    const invView = this.invertMatrix(view);

    // Ray in clip space
    const rayClip = [x, y, -1, 1];

    // Ray in eye space
    const rayEye = this.multiplyMatrixVector(invProjection, rayClip);
    rayEye[2] = -1;
    rayEye[3] = 0;

    // Ray in world space
    const rayWorld = this.multiplyMatrixVector(invView, rayEye);
    const rayDir = this.normalize([rayWorld[0], rayWorld[1], rayWorld[2]]);

    // Camera position
    const centerX = 0;
    const centerY = 0;
    const centerZ = 0;
    const camX = centerX + Math.cos(this.cameraAngle) * this.cameraDistance + this.panX;
    const camY = this.cameraHeight + this.panY;
    const camZ = centerZ + Math.sin(this.cameraAngle) * this.cameraDistance;

    // Ray march to find first voxel intersection
    const maxDistance = 500;
    const stepSize = 0.5; // Step along ray in world units

    for (let dist = 0; dist < maxDistance; dist += stepSize) {
      const rayX = camX + rayDir[0] * dist;
      const rayY = camY + rayDir[1] * dist;
      const rayZ = camZ + rayDir[2] * dist;

      // Convert to grid coordinates
      const gridX = Math.floor(rayX + this.gridWidth / 2);
      const gridY = Math.floor(rayZ + this.gridHeight / 2);

      // Check bounds
      if (gridX < 0 || gridX >= this.gridWidth || gridY < 0 || gridY >= this.gridHeight) {
        continue;
      }

      // Check if ray hits sand at this position
      const sandHeight = Math.floor(this.currentSandGrid.get(gridX, gridY));
      if (rayY <= sandHeight && rayY >= 0) {
        return { x: gridX, y: gridY };
      }
    }

    return null;
  }

  public pan(deltaX: number, deltaY: number): void {
    this.panX += deltaX * 0.1;
    this.panY -= deltaY * 0.1;
  }

  public zoom(delta: number): void {
    this.zoomLevel = Math.max(0.5, Math.min(3.0, this.zoomLevel + delta));
  }

  public drawHoverPreview(worldCoord: Coordinate, brushSize: number): void {
    const gl = this.gl;
    if (!gl || !this.program || !this.canvas || !this.currentSandGrid) return;

    // Get the height at the target position
    const sandHeight = Math.floor(this.currentSandGrid.get(worldCoord.x, worldCoord.y));

    // Set up matrices
    const aspect = this.canvas.width / this.canvas.height;
    const projection = this.createPerspectiveMatrix(Math.PI / 4, aspect, 1, 1000);
    const view = this.createViewMatrix();

    const projLoc = gl.getUniformLocation(this.program, 'uProjection');
    const viewLoc = gl.getUniformLocation(this.program, 'uView');
    const voxelSizeLoc = gl.getUniformLocation(this.program, 'uVoxelSize');
    const offsetLoc = gl.getUniformLocation(this.program, 'uInstanceOffset');
    const colorLoc = gl.getUniformLocation(this.program, 'uInstanceColor');

    gl.uniformMatrix4fv(projLoc, false, projection);
    gl.uniformMatrix4fv(viewLoc, false, view);
    gl.uniform1f(voxelSizeLoc, 1.0 * this.zoomLevel);

    // Draw wireframe boxes for brush area
    gl.disable(gl.DEPTH_TEST); // Draw on top
    gl.lineWidth(2);

    const posLoc = gl.getAttribLocation(this.program, 'aPosition');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.voxelBuffer);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 3, gl.FLOAT, false, 0, 0);

    const normalLoc = gl.getAttribLocation(this.program, 'aNormal');
    gl.bindBuffer(gl.ARRAY_BUFFER, this.normalBuffer);
    gl.enableVertexAttribArray(normalLoc);
    gl.vertexAttribPointer(normalLoc, 3, gl.FLOAT, false, 0, 0);

    // Draw highlight for cells in brush radius
    for (let dy = -brushSize; dy <= brushSize; dy++) {
      for (let dx = -brushSize; dx <= brushSize; dx++) {
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > brushSize) continue;

        const targetX = worldCoord.x + dx;
        const targetY = worldCoord.y + dy;

        if (targetX < 0 || targetX >= this.gridWidth || targetY < 0 || targetY >= this.gridHeight) {
          continue;
        }

        const height = Math.floor(this.currentSandGrid.get(targetX, targetY));

        // Draw a highlight voxel at the top of the sand column
        gl.uniform3f(
          offsetLoc,
          targetX - this.gridWidth / 2,
          height,
          targetY - this.gridHeight / 2
        );

        // White with transparency for highlight
        const alpha = 1.0 - (dist / brushSize) * 0.5;
        gl.uniform3f(colorLoc, alpha, alpha, 0.0);

        gl.drawArrays(gl.TRIANGLES, 0, 36);
      }
    }

    gl.enable(gl.DEPTH_TEST);
  }

  public dispose(): void {
    const gl = this.gl;

    window.removeEventListener('resize', this.handleResize);

    if (this.canvas) {
      this.canvas.removeEventListener('mousedown', this.handleMouseDown);
      this.canvas.removeEventListener('mousemove', this.handleMouseMove);
      this.canvas.removeEventListener('mouseup', this.handleMouseUp);
      this.canvas.removeEventListener('wheel', this.handleWheel);
    }

    if (gl) {
      if (this.voxelBuffer) gl.deleteBuffer(this.voxelBuffer);
      if (this.normalBuffer) gl.deleteBuffer(this.normalBuffer);
      if (this.instanceOffsetBuffer) gl.deleteBuffer(this.instanceOffsetBuffer);
      if (this.instanceColorBuffer) gl.deleteBuffer(this.instanceColorBuffer);
      if (this.instanceAOBuffer) gl.deleteBuffer(this.instanceAOBuffer);
      if (this.program) gl.deleteProgram(this.program);

      // Clean up particle resources
      if (this.particleBuffer) gl.deleteBuffer(this.particleBuffer);
      if (this.particleInstanceBuffer) gl.deleteBuffer(this.particleInstanceBuffer);
      if (this.particleColorBuffer) gl.deleteBuffer(this.particleColorBuffer);
      if (this.particleProgram) gl.deleteProgram(this.particleProgram);
    }

    if (this.canvas && this.container) {
      this.container.removeChild(this.canvas);
    }

    this.canvas = null;
    this.gl = null;
    this.container = null;
  }
}
